import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, CheckCheck, BellRing, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { ensurePushSubscription } from '../lib/pushClient';
import { translateNotificationTitle, translateNotificationMessage } from '../lib/notifications';
import { formatDateTimeKSA } from '../lib/dates';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 20000; // 20s lightweight polling

export const NotificationsBell = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const dropdownRef = useRef(null);
  const authDeadRef = useRef(false);

  const fetchUnreadCount = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/notifications/unread-count`, {
        withCredentials: true
      });
      setUnread(r.data?.unread_count ?? 0);
    } catch (e) {
      // Aug 2026 prod-health — session expired: stop the 20s loop so a
      // stale overnight tab stops generating guaranteed 401s.
      if (e?.response?.status === 401) authDeadRef.current = true;
    }
  };

  // Feb 2026 — Contact Finance via WhatsApp. Any notification carrying
  // `category === 'subscription'` gets the button. Endpoint is per-caller
  // so no student_id needs to be passed from the client.
  const openFinanceWhatsApp = async (notificationId) => {
    try {
      const r = await axios.get(`${API_URL}/api/me/finance-contact/whatsapp-link`, {
        withCredentials: true,
      });
      if (r.data?.url) window.open(r.data.url, '_blank', 'noopener,noreferrer');
      // Fire-and-forget analytics — audits Track-A vs. in-app effectiveness.
      // Wrapped in its own try so a failed ping never blocks the open.
      try {
        await axios.post(
          `${API_URL}/api/me/finance-contact/clicked`,
          { source: 'in_app', notification_id: notificationId || null },
          { withCredentials: true },
        );
      } catch (_) { /* non-blocking */ }
    } catch (e) {
      // Non-blocking — the notification action was a convenience; a
      // failure here should not close the drawer or interrupt reading.
      // eslint-disable-next-line no-console
      console.warn('Finance WhatsApp link fetch failed', e);
    }
  };

  const fetchList = async () => {
    try {
      // Use the enriched /feed endpoint (Feb 2026 — Phase 4) so we get
      // category + link + metadata for the new bell UI. Falls back to
      // the legacy endpoint on 404 so older deployments still work.
      const r = await axios.get(`${API_URL}/api/notifications/feed?limit=20`, {
        withCredentials: true
      });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      try {
        const r = await axios.get(`${API_URL}/api/notifications/?limit=20`, { withCredentials: true });
        setItems(Array.isArray(r.data) ? r.data : []);
      } catch {
        setItems([]);
      }
    }
  };

  // Jun 2026 infra fix — poll ONLY when authenticated. Unauthenticated
  // mounts used to hammer /unread-count every 20s with guaranteed 4xx.
  useEffect(() => {
    if (!user) return undefined;
    fetchUnreadCount();
    const id = setInterval(() => {
      if (!authDeadRef.current && !document.hidden) fetchUnreadCount();
    }, POLL_MS);
    const onVis = () => { if (!document.hidden && !authDeadRef.current) fetchUnreadCount(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Jun 2026 — cross-tab sync: when another tab opens the bell (auto-read),
  // zero this tab's badge instantly via the storage event.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'mesp_bell_read_at') setUnread(0);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const togglePanel = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      await fetchList();
      // Jun 2026 — Auto-Read: opening the bell zeroes the counter
      // globally (server-side mark-all-read + cross-tab broadcast).
      // Items keep their unread highlight for this viewing session.
      setUnread(0);
      try { localStorage.setItem('mesp_bell_read_at', String(Date.now())); } catch (_) { /* private mode */ }
      try {
        await axios.patch(`${API_URL}/api/notifications/mark-all-read`, {}, { withCredentials: true });
      } catch (_) { /* non-blocking */ }
    }
  };

  const markRead = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/notifications/${id}/read`, {}, {
        withCredentials: true
      });
      setItems((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
      setUnread((c) => Math.max(0, c - 1));
    } catch (e) {
      // ignore
    }
  };

  const markAllRead = async () => {
    try {
      // Single round-trip via the new mark-all-read endpoint.
      await axios.patch(`${API_URL}/api/notifications/mark-all-read`, {}, { withCredentials: true });
    } catch {
      // fallback: one PATCH per row
      const unreadIds = items.filter((n) => !n.is_read).map((n) => n.id);
      await Promise.all(unreadIds.map((id) =>
        axios.patch(`${API_URL}/api/notifications/${id}/read`, {}, { withCredentials: true })
          .catch(() => null)
      ));
    }
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnread(0);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={togglePanel}
        className="p-2 text-[#5C6B8C] hover:bg-[#F5F5F0] rounded-sm transition-colors relative"
        data-testid="notifications-bell-btn"
        aria-label={t('Notifications', 'الإشعارات')}
      >
        <Bell size={20} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-[#C86A53] text-white text-[10px] font-semibold rounded-full flex items-center justify-center"
            data-testid="notifications-unread-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Mobile-only backdrop: tap anywhere outside the panel to close. */}
          <div
            className="fixed inset-0 z-40 sm:hidden bg-black/10"
            onClick={() => setOpen(false)}
            data-testid="notifications-backdrop"
            aria-hidden="true"
          />
          <div
            className={`fixed inset-x-2 top-16 sm:absolute sm:inset-x-auto sm:top-auto ${language === 'ar' ? 'sm:left-0' : 'sm:right-0'} sm:mt-2 sm:w-[360px] sm:max-w-[92vw] bg-[#121A33] border border-[#1E2A4A] rounded-sm shadow-xl z-50 max-h-[calc(100vh-5rem)] flex flex-col`}
            data-testid="notifications-panel"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#1E2A4A] flex-shrink-0">
              <h3 className="text-sm font-semibold text-white">
                {t('Notifications', 'الإشعارات')}
              </h3>
              <div className="flex items-center gap-1">
                {typeof Notification !== 'undefined' && Notification.permission !== 'granted' && (
                  <button
                    type="button"
                    onClick={async () => {
                      const r = await ensurePushSubscription();
                      if (!r.ok && r.reason === 'permission_denied') {
                        toast.error(t('Please enable notifications in your browser settings.', 'يرجى تفعيل الإشعارات من إعدادات المتصفح.'));
                      }
                    }}
                    className="p-2 text-[#C9A24E] hover:bg-white/10 rounded-sm transition-colors"
                    title={t('Enable push notifications', 'تفعيل إشعارات الجوال')}
                    data-testid="enable-push-btn"
                  >
                    <BellRing size={16} />
                  </button>
                )}
                {items.some((n) => !n.is_read) && (
                  <button
                    onClick={markAllRead}
                    className="px-2 py-1.5 text-xs text-[#A0AABF] hover:text-white flex items-center gap-1"
                    data-testid="notifications-mark-all-read-btn"
                  >
                    <CheckCheck size={14} />
                    {t('Mark all read', 'تعليم الكل')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                  className="p-2 -m-1 text-[#A0AABF] hover:text-white hover:bg-white/10 rounded-sm min-w-[40px] min-h-[40px] flex items-center justify-center"
                  data-testid="notifications-close-btn"
                  aria-label={t('Close', 'إغلاق')}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1" data-testid="notifications-list">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-[#A0AABF]">
                {t('No notifications', 'لا توجد إشعارات')}
              </div>
            ) : (
              items.map((n) => {
                const tone = n.category === 'urgent'
                  ? 'border-r-4 border-r-red-500'
                  : n.category === 'important'
                    ? 'border-r-4 border-r-amber-500'
                    : 'border-r-4 border-r-[#C9A24E]';
                // Feb 2026 — Contact Finance action appears on every
                // subscription-category notification (freeze / expiry /
                // grace / reminder / etc). Uses the existing category
                // tag so no additional flag is needed on the row.
                const isFinance = n.category === 'subscription';
                return (
                <div
                  key={n.id}
                  className={`border-b border-[#1E2A4A] transition-colors ${
                    !n.is_read ? 'bg-white/[0.03]' : ''
                  } ${tone}`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <button
                    onClick={() => {
                      if (!n.is_read) markRead(n.id);
                      setOpen(false);
                      const target = n.link || '/dashboard';
                      if (/^https?:\/\//i.test(target)) {
                        window.location.href = target;
                      } else {
                        navigate(target);
                      }
                    }}
                    className={`w-full text-${language === 'ar' ? 'right' : 'left'} px-4 py-3 hover:bg-white/5 transition-colors`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!n.is_read ? 'font-semibold text-white' : 'text-[#A0AABF]'} truncate`}>
                          {translateNotificationTitle(n.title, language)}
                        </p>
                        <p className="text-xs text-[#A0AABF] mt-1 line-clamp-2">
                          {translateNotificationMessage(n.body || n.message || '', language)}
                        </p>
                        <p className="text-[10px] text-[#6B82A0] mt-1">
                          {formatDateTimeKSA(n.created_at, language)}
                        </p>
                      </div>
                      {!n.is_read && (
                        <span className="mt-1 w-2 h-2 bg-[#C9A24E] rounded-full flex-shrink-0"></span>
                      )}
                    </div>
                  </button>
                  {isFinance && (
                    <div className={`px-4 pb-3 -mt-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openFinanceWhatsApp(n.id); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[#25D366] text-white text-xs font-semibold hover:bg-[#1DA851] transition-colors"
                        data-testid={`notification-finance-contact-${n.id}`}
                      >
                        <MessageCircle size={13} />
                        {t('Contact Finance', 'التواصل مع المالية')}
                      </button>
                    </div>
                  )}
                </div>
                );
              })
            )}
          </div>
          </div>
        </>
      )}
    </div>
  );
};
